import { useState, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";
import {
  Sun,
  Heart,
  Home,
  AlertCircle,
  Briefcase,
  Clock,
  Calendar,
  CheckCircle,
  TrendingUp,
} from "lucide-react";

const apiBaseURL = process.env.REACT_APP_BASE_URL;
const leavesBasePath = "/leaves";

// ===== NORMALIZE INFINITY CODE =====
export const normalizeInfinityCodeValue = (value) => {
  if (value === undefined || value === null) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.length) return null;
    const lowered = trimmed.toLowerCase();
    if (
      lowered === "null" ||
      lowered === "undefined" ||
      lowered === "nan" ||
      lowered === "true" ||
      lowered === "false"
    ) {
      return null;
    }
    return trimmed;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }

  if (typeof value === "object") {
    const nestedValue =
      value.infinityCode ??
      value.employeeInfinityCode ??
      value.employeeCode ??
      value.code ??
      null;

    if (nestedValue !== null && nestedValue !== undefined) {
      return normalizeInfinityCodeValue(nestedValue);
    }

    return null;
  }

  const fallback = String(value).trim();
  if (!fallback.length) return null;
  const loweredFallback = fallback.toLowerCase();
  if (
    loweredFallback === "[object object]" ||
    loweredFallback === "null" ||
    loweredFallback === "undefined" ||
    loweredFallback === "nan" ||
    loweredFallback === "true" ||
    loweredFallback === "false"
  ) {
    return null;
  }
  return fallback;
};

// ===== GET INFINITY CODE =====
export const getInfinityCode = (employeeData, profile) => {
  const fromProps = normalizeInfinityCodeValue(employeeData?.infinityCode);
  if (fromProps !== null) return fromProps;
  const fromProfile = normalizeInfinityCodeValue(profile?.infinityCode);
  if (fromProfile !== null) return fromProfile;
  const fromStorage = normalizeInfinityCodeValue(localStorage.getItem("infinityCode"));
  return fromStorage;
};

// ===== RESOLVE YEAR =====
export const resolveYear = () => {
  const y = Number(localStorage.getItem("leaveYear"));
  return Number.isFinite(y) && y > 2000 ? y : new Date().getFullYear();
};

// ===== NORMALIZE LEAVE DATA =====
export const normalizeLeaveData = (leaves) => {
  return leaves.map((leave) => ({
    ...leave,
    type: leave.type || leave.leaveType,
    leaveType: leave.leaveType || leave.type,
  }));
};

// ===== CREATE DEFAULT LEAVE TYPES =====
// ===== CREATE DEFAULT LEAVE TYPES =====
export const createDefaultLeaveTypes = (theme, t) => {
  // Safety checks for theme and t
  if (!theme || !t) {
    console.warn("[createDefaultLeaveTypes] Missing theme or translations", { theme, t });
    return [];
  }

  if (!theme.colors) {
    console.warn("[createDefaultLeaveTypes] theme.colors is missing");
    return [];
  }

  if (!t.leaveTypes) {
    console.warn("[createDefaultLeaveTypes] t.leaveTypes is missing");
    return [];
  }

  const types = [
    {
      id: "annual",
      name: t?.leaveTypes?.annual || "Annual Leave",
      icon: Sun,
      color: theme?.colors?.warning || "#f59e0b",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.annual || "",
    },
    {
      id: "sick",
      name: t?.leaveTypes?.sick || "Sick Leave",
      icon: Heart,
      color: theme?.colors?.danger || "#ef4444",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.sick || "",
    },
    {
      id: "maternity",
      name: t?.leaveTypes?.maternity || "Maternity Leave",
      icon: Home,
      color: theme?.colors?.success || "#10b981",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.maternity || "",
    },
    {
      id: "emergency",
      name: t?.leaveTypes?.emergency || "Emergency Leave",
      icon: AlertCircle,
      color: theme?.colors?.info || "#0891b2",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.emergency || "",
    },
    {
      id: "wfh",
      name: t?.leaveTypes?.workFromHome || "Work From Home",
      icon: Briefcase,
      color: "#2e7cba",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.workFromHome || "",
    },
    {
      id: "partialDay",
      name: t?.leaveTypes?.partialDay || "Partial Day",
      icon: Clock,
      color: "#0101fa",
      available: 0,
      used: 0,
      description: t?.leaveDescriptions?.partialDay || "",
    },
  ];

  // Final validation: ensure all types have icon
  return types.filter(t => t && t.icon && t.id);
};

// ===== MAIN HOOK =====
const useLeaveData = ({ employeeData, profile, theme, language, t }) => {
  const quotaEnsuredRef = useRef({});

  const [leaveTypes, setLeaveTypes] = useState(() => createDefaultLeaveTypes(theme, t));
  const [leaveStats, setLeaveStats] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [employeeImages, setEmployeeImages] = useState({});

  const ensureLeaveQuota = async (infinityCode, year) => {
    const normalizedCode = normalizeInfinityCodeValue(infinityCode);
    if (!normalizedCode) return;
    const key = `${normalizedCode}:${year}`;
    if (quotaEnsuredRef.current[key]) return;
    try {
      const res = await apiFetch(
        `${apiBaseURL}/api${leavesBasePath}/${encodeURIComponent(normalizedCode)}/quota/${encodeURIComponent(year)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!res.ok) throw new Error("Failed to ensure quota");
      quotaEnsuredRef.current[key] = true;
    } catch (e) {
      quotaEnsuredRef.current[key] = true;
      if (process.env.NODE_ENV !== "production") {
        console.warn("[LeavePage] ensureLeaveQuota warning:", e?.status || e?.message);
      }
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const rawInfinityCode = getInfinityCode(employeeData, profile);
      const resolvedInfinityCode = normalizeInfinityCodeValue(rawInfinityCode);
      const year = resolveYear();

      if (resolvedInfinityCode === null) {
        setErrorMessage(
          language === "ar"
            ? "لم يتم العثور على معرّف الموظف. يرجى تقديم بيانات الموظف أو تعيين infinityCode."
            : "Employee ID not found. Please provide employee data or set infinityCode."
        );
        setIsLoading(false);
        return;
      }

      await ensureLeaveQuota(resolvedInfinityCode, year);

      const encodedInfinityCode = encodeURIComponent(resolvedInfinityCode);

      // Quota
      let quotaResponse = await apiFetch(
        `${apiBaseURL}/api${leavesBasePath}/${encodedInfinityCode}/quota?year=${encodeURIComponent(year)}`,
        { method: "GET" }
      );
      if (!quotaResponse.ok) {
        quotaResponse = await apiFetch(
          `${apiBaseURL}/api${leavesBasePath}/${encodedInfinityCode}/quota/${encodeURIComponent(year)}`,
          { method: "GET" }
        );
      }
      const quotaPayload = quotaResponse.ok ? await quotaResponse.json() : {};
      const q = quotaPayload?.data || quotaPayload?.quota || quotaPayload || {};

      setLeaveTypes([
        {
          id: "annual",
          name: t.leaveTypes.annual,
          icon: Sun,
          color: theme.colors.warning,
          available: Number(q.annualQuota) || 0,
          used: Number(q.annualUsed) || 0,
          description: t.leaveDescriptions.annual,
        },
        {
          id: "sick",
          name: t.leaveTypes.sick,
          icon: Heart,
          color: theme.colors.danger,
          available: Number(q.sickQuota) || 0,
          used: Number(q.sickUsed) || 0,
          description: t.leaveDescriptions.sick,
        },
        {
          id: "maternity",
          name: t.leaveTypes.maternity,
          icon: Home,
          color: theme.colors.success,
          available: Number(q.maternityQuota) || 0,
          used: Number(q.maternityUsed) || 0,
          description: t.leaveDescriptions.maternity,
        },
        {
          id: "emergency",
          name: t.leaveTypes.emergency,
          icon: AlertCircle,
          color: theme.colors.info,
          available: Number(q.emergencyQuota) || 0,
          used: Number(q.emergencyUsed) || 0,
          description: t.leaveDescriptions.emergency,
        },
        {
          id: "wfh",
          name: t.leaveTypes.workFromHome,
          icon: Briefcase,
          color: "#2e7cba",
          available: Number(q.workFromHomeQuota) || 0,
          used: Number(q.workFromHomeUsed) || 0,
          description: t.leaveDescriptions.workFromHome,
        },
        {
          id: "partialDay",
          name: t.leaveTypes.partialDay,
          icon: Clock,
          color: "#8b5cf6",
          available: Number(q.annualQuota) || 0,
          used: Number(q.annualUsed) || 0,
          description: t.leaveDescriptions.partialDay,
        },
      ]);

      // Balance
      const balanceRes = await apiFetch(
        `${apiBaseURL}/api${leavesBasePath}/${encodedInfinityCode}/balance?year=${encodeURIComponent(year)}`,
        { method: "GET" }
      );
      const bPayload = balanceRes.ok ? await balanceRes.json() : {};
      const b = bPayload?.data || bPayload || {};
      const annualTotal = b?.quotas?.annual?.total ?? 0;
      const annualUsed = b?.quotas?.annual?.used ?? 0;
      const annualRemaining =
        b?.quotas?.annual?.remaining ?? Math.max(annualTotal - annualUsed, 0);
      const recentApproved = Array.isArray(b?.recentApprovedLeaves)
        ? b.recentApprovedLeaves
        : [];
      const recentAvg = recentApproved.length
        ? (
            recentApproved.reduce((acc, l) => acc + (l.days ?? 0), 0) /
            recentApproved.length
          ).toFixed(1)
        : "0";

      setLeaveStats([
        {
          icon: Calendar,
          value: annualTotal,
          label: t.stats.totalAvailable,
          color: theme.colors.primary,
          trend: t.stats.annualAllocation,
          subtitle: t.stats.daysPerYear,
        },
        {
          icon: CheckCircle,
          value: annualUsed,
          label: t.stats.daysUsed,
          color: theme.colors.success,
          trend: `${annualTotal ? Math.round((annualUsed / annualTotal) * 100) : 0}% ${t.stats.utilized}`,
          subtitle: t.stats.thisYear,
        },
        {
          icon: Clock,
          value: annualRemaining,
          label: t.stats.daysRemaining,
          color: theme.colors.warning,
          trend: t.stats.availableNow,
          subtitle: t.stats.untilDec,
        },
        {
          icon: TrendingUp,
          value: recentAvg,
          label: t.stats.averageDuration,
          color: theme.colors.info,
          trend: t.stats.perRequest,
          subtitle: t.stats.days,
        },
      ]);

      // History
      const historyRes = await apiFetch(
        `${apiBaseURL}/api${leavesBasePath}?infinityCode=${encodedInfinityCode}&year=${encodeURIComponent(year)}`,
        { method: "GET" }
      );
      let leaves = [];
      if (historyRes.ok) {
        const histPayload = await historyRes.json();
        if (histPayload?.success && histPayload?.data) {
          if (Array.isArray(histPayload.data.leaves)) {
            leaves = histPayload.data.leaves;
          } else if (Array.isArray(histPayload.data)) {
            leaves = histPayload.data;
          }
        } else if (Array.isArray(histPayload)) {
          leaves = histPayload;
        } else if (Array.isArray(histPayload?.data)) {
          leaves = histPayload.data;
        }
      }

      console.log("📋 Raw leaves from API:", leaves);
      console.log("📋 Leaves count:", leaves.length);
      console.log("📋 All leaves details:", JSON.stringify(leaves, null, 2));
      console.log("📋 Approved leaves:", leaves.filter((l) => l.status === "approved"));
      console.log("📋 Pending leaves:", leaves.filter((l) => l.status === "pending"));
      console.log("📋 All statuses:", leaves.map((l) => ({
        id: l.id,
        type: l.leaveType || l.type,
        status: l.status,
        startDate: l.startDate,
        endDate: l.endDate,
        createdAt: l.createdAt,
      })));
      console.log("📋 Work From Home leaves:", leaves.filter((l) => l.leaveType === "wfh" || l.type === "wfh"));
      console.log("📋 All leave types in data:", [...new Set(leaves.map((l) => l.leaveType || l.type))]);

      setLeaveHistory(normalizeLeaveData(leaves));
    } catch (err) {
      console.error("Error fetching leave data:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          (language === "ar"
            ? "فشل تحميل بيانات الإجازات"
            : "Failed to load leave data")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolidays = async () => {
    const year = new Date().getFullYear();
    const countryCode = "EG";
    const today = new Date();

    let externalHolidays = [];
    let backendHolidays = [];

    // STEP 1: Fetch from external API
    try {
      console.log(`🌍 Fetching holidays from external API for ${countryCode} ${year}...`);
      const externalRes = await fetch(
        `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
      );
      if (externalRes.ok) {
        const data = await externalRes.json();
        externalHolidays = data.map((holiday) => {
          const holidayDate = new Date(holiday.date);
          const diff = Math.ceil((holidayDate - today) / (1000 * 60 * 60 * 24));
          return { ...holiday, daysUntil: diff, source: "external" };
        });
        console.log(`✅ Fetched ${externalHolidays.length} holidays from external API`);
      } else {
        console.warn(`⚠️ External API returned ${externalRes.status}`);
      }
    } catch (externalError) {
      console.warn("⚠️ External API failed:", externalError.message);
    }

    // STEP 2: Fetch from backend API
    try {
      console.log("🔄 Fetching holidays from backend API...");
      const backendRes = await apiFetch(`${apiBaseURL}/api/leaves/holidays/${year}`, { method: "GET" });
      if (!backendRes.ok) throw new Error(`Backend API returned ${backendRes.status}`);

      const backendData = await backendRes.json();
      console.log("📦 Backend response:", backendData);

      let holidaysArray = [];

      if (backendData.success && backendData.data) {
        if (backendData.data.success && Array.isArray(backendData.data.data)) {
          holidaysArray = backendData.data.data;
          console.log("✓ Found holidays in data.data.data");
        } else if (Array.isArray(backendData.data)) {
          holidaysArray = backendData.data;
          console.log("✓ Found holidays in data.data");
        } else if (backendData.data.holidays && Array.isArray(backendData.data.holidays)) {
          holidaysArray = backendData.data.holidays;
          console.log("✓ Found holidays in data.data.holidays");
        }
      } else if (Array.isArray(backendData)) {
        holidaysArray = backendData;
        console.log("✓ Found holidays directly in response");
      }

      if (holidaysArray.length > 0) {
        backendHolidays = holidaysArray.map((holiday) => {
          const holidayDate = new Date(holiday.date);
          const diff = Math.ceil((holidayDate - today) / (1000 * 60 * 60 * 24));
          return {
            date: holiday.date,
            localName: holiday.nameArabic || holiday.name,
            name: holiday.name,
            countryCode: countryCode,
            fixed: holiday.isRecurring ?? false,
            global: holiday.type === "national",
            type: holiday.type,
            daysUntil: diff,
            source: "backend",
            notes: holiday.notes,
          };
        });
        console.log(`✅ Fetched ${backendHolidays.length} holidays from backend`);
      } else {
        console.warn("⚠️ Backend returned no holidays. Response structure:", JSON.stringify(backendData, null, 2));
      }
    } catch (backendError) {
      console.error("❌ Backend API failed:", backendError.message);
    }

    // STEP 3: Merge and deduplicate
    const mergedHolidays = [...backendHolidays, ...externalHolidays];
    const uniqueHolidays = mergedHolidays.reduce((acc, holiday) => {
      const existing = acc.find((h) => h.date === holiday.date);
      if (!existing) {
        acc.push(holiday);
      } else if (holiday.source === "backend" && existing.source === "external") {
        const index = acc.indexOf(existing);
        acc[index] = holiday;
      }
      return acc;
    }, []);

    uniqueHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    console.log(`🎉 Total unique holidays: ${uniqueHolidays.length}`);
    setHolidays(uniqueHolidays);
  };

  const loadEmployeeImages = async (leaveHistory, activeTab) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL || "";
    const allInfinityCodes = new Set();

    leaveHistory.forEach((leave) => {
      if (leave.infinityCode) allInfinityCodes.add(leave.infinityCode);
    });

    const allLeaveElements = document.querySelectorAll("[data-infinity-code]");
    allLeaveElements.forEach((el) => {
      const code = el.getAttribute("data-infinity-code");
      if (code) allInfinityCodes.add(code);
    });

    const uniqueCodes = Array.from(allInfinityCodes);
    console.log("🖼️ Loading images for infinityCodes:", uniqueCodes);

    for (const infinityCode of uniqueCodes) {
      if (employeeImages[infinityCode]) {
        console.log(`✅ Image already loaded for ${infinityCode}`);
        continue;
      }

      try {
        const imageUrl = `${BASE_URL}/employees/pic/${infinityCode}`;
        console.log(`📥 Fetching image from: ${imageUrl}`);

        const response = await apiFetch(imageUrl, { method: "GET" });

        console.log(`📊 Response for ${infinityCode}:`, {
          status: response.status,
          ok: response.ok,
          contentType: response.headers.get("content-type"),
        });

        if (response.ok) {
          const blob = await response.blob();
          console.log(`📦 Blob size for ${infinityCode}: ${blob.size} bytes, type: ${blob.type}`);

          if (blob.size > 0) {
            const objectURL = URL.createObjectURL(blob);
            console.log(`✅ Created object URL for ${infinityCode}: ${objectURL}`);
            setEmployeeImages((prev) => ({
              ...prev,
              [infinityCode]: objectURL,
            }));
          } else {
            console.warn(`⚠️ Empty blob received for ${infinityCode}`);
          }
        } else {
          console.warn(`❌ Failed to load image for ${infinityCode}: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error(`❌ Error loading image for ${infinityCode}:`, error);
      }
    }
  };

  return {
    leaveTypes,
    leaveStats,
    leaveHistory,
    holidays,
    isLoading,
    errorMessage,
    successMessage,
    employeeImages,
    setErrorMessage,
    setSuccessMessage,
    fetchAllData,
    fetchHolidays,
    loadEmployeeImages,
    setLeaveHistory,
  };
};

export default useLeaveData;